import { msalInstance, MSAL_SCOPES } from '@config/msalConfig';
import { setMsalSession, clearMsalSession } from '@config/msalSession';
import { isWeb } from '@helpers';
import { unlogin } from '@reducers/utils';
import { sendLog } from '@reducers/dataDogs';
import { LOGGER_TITLES } from '@reducers/dataDogs/constants';
import { reinitializeAppState } from './index';
import type { AppThunk } from '@store/types';
import type { AnyAction } from '@reduxjs/toolkit';

// Web multi-account thunks using MSAL's built-in multi-account management.
// Native multi-account uses ROPC flow via selectNewAccount — these are web-only.

export const addWebAccount = (): AppThunk<Promise<void>> => async (dispatch) => {
  if (!isWeb) return;

  let result;
  try {
    result = await msalInstance.loginPopup({
      scopes: MSAL_SCOPES,
      prompt: 'login',
    });
  } catch (e) {
    // loginPopup failed (user cancelled or B2C error) — previous session is untouched, nothing to recover
    dispatch(sendLog({ title: LOGGER_TITLES.ADD_WEB_ACCOUNT_LOGIN_POPUP_FAILED, data: { error: String(e) }, status: 'error' }) as unknown as AnyAction);
    return;
  }

  if (!result.account) return;

  setMsalSession(result.account, result.accessToken);

  try {
    await dispatch(reinitializeAppState());
  } catch (e) {
    // setMsalSession already wrote token + _sf_active_account_id; clear them so the tab's
    // sessionStorage is consistent regardless of how the caller recovers (unlogin / showBoundary).
    clearMsalSession();
    dispatch(sendLog({ title: LOGGER_TITLES.ADD_WEB_ACCOUNT_REINITIALIZE_FAILED, data: { error: String(e) }, status: 'error' }) as unknown as AnyAction);
    throw e;
  }
};

export const switchWebAccount =
  (homeAccountId: string): AppThunk<Promise<void>> =>
  async (dispatch) => {
    if (!isWeb) return;

    const allAccounts = msalInstance.getAllAccounts();
    const account = allAccounts.find((a) => a.homeAccountId === homeAccountId);

    if (!account) {
      dispatch(sendLog({ title: LOGGER_TITLES.SWITCH_WEB_ACCOUNT_NOT_FOUND, data: { homeAccountId }, status: 'error' }) as unknown as AnyAction);
      return;
    }

    // Token acquisition: silent, fallback to popup
    let tokenData;
    try {
      tokenData = await msalInstance.acquireTokenSilent({ scopes: MSAL_SCOPES, account });
    } catch (silentError) {
      try {
        tokenData = await msalInstance.acquireTokenPopup({ scopes: MSAL_SCOPES, account });
      } catch (popupError) {
        // Neither silent nor popup produced a token — state is untouched, nothing to recover
        dispatch(sendLog({ title: LOGGER_TITLES.SWITCH_WEB_ACCOUNT_TOKEN_FAILED, data: { homeAccountId, error: String(popupError) }, status: 'error' }) as unknown as AnyAction);
        return;
      }
    }

    // From here on, reinitializeAppState clears Redux — on failure we undo our own
    // sessionStorage writes and rethrow so the caller can surface the failure
    // (currently unlogin; eventually an AccountSwitchError boundary).
    setMsalSession(account, tokenData.accessToken);
    try {
      await dispatch(reinitializeAppState());
    } catch (e) {
      clearMsalSession();
      dispatch(sendLog({ title: LOGGER_TITLES.SWITCH_WEB_ACCOUNT_REINITIALIZE_FAILED, data: { homeAccountId, error: String(e) }, status: 'error' }) as unknown as AnyAction);
      throw e;
    }
  };

export const removeWebAccount =
  (homeAccountId: string): AppThunk<Promise<void>> =>
  async (dispatch) => {
    if (!isWeb) return;

    const allAccounts = msalInstance.getAllAccounts();
    const account = allAccounts.find((a) => a.homeAccountId === homeAccountId);

    if (!account) return;

    const isActive = msalInstance.getActiveAccount()?.homeAccountId === homeAccountId;
    const remainingAccounts = allAccounts.filter((a) => a.homeAccountId !== homeAccountId);

    if (remainingAccounts.length === 0) {
      await dispatch(unlogin());
      return;
    }

    if (isActive) {
      await dispatch(switchWebAccount(remainingAccounts[0].homeAccountId));
    }

    try {
      await msalInstance.logoutPopup({
        account,
        mainWindowRedirectUri: window.origin,
      });
    } catch (e) {
      dispatch(sendLog({ title: LOGGER_TITLES.REMOVE_WEB_ACCOUNT_LOGOUT_FAILED, data: { homeAccountId, error: String(e) }, status: 'error' }) as unknown as AnyAction);
    }
  };
